/*
 * Subscription-aware checkout redirect.
 *
 * Applies the cart's discount codes on the way to checkout through
 * /checkout?discount=A,B. Code sources:
 *   - window.__pdpDiscountOverride.couponCode on subscription carts, and its OTP
 *     code on one-time carts when the PDP section turns that on
 *   - _sub_discount_code on subscription lines (_plan_upgrade_code once upgraded)
 *   - _otp_discount_code on lines stamped _auto_apply_otp_discount_code=true
 *   - _unlock_discount_code on a _free_unlock gift line
 *   - _upsell_discount_code on dialog upsell lines
 *
 * Covers the cart drawer checkout form, the /checkout anchors and
 * window.SubscriptionCheckout.redirectToCheckout (PBFCM straight to checkout,
 * quiz buy box, Build Box checkout mode).
 */
(function () {
  var LOG_PREFIX = '[autoApplyingDiscount]';
  var SUBSCRIPTION_PROPERTY_KEY = '_is_subscription_purchase';
  var ONE_TIME_PROPERTY_KEY = '_is_one_time_purchase';
  var OTP_AUTO_APPLY_PROPERTY_KEY = '_auto_apply_otp_discount_code';
  var BUSY_CLASS = 'fd-checkout-busy';

  function normalizeBool(v) {
    if (typeof v === 'string') {
      var s = v.trim().toLowerCase();
      return s === 'true' || s === '1' || s === 'yes';
    }
    return Boolean(v);
  }

  function isSubscriptionItem(item) {
    if (!item) return false;
    var props = item.properties || {};
    return normalizeBool(props[SUBSCRIPTION_PROPERTY_KEY])
      || Boolean(item.selling_plan)
      || Boolean(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
  }

  function isGiftOrAddOnItem(item) {
    var props = (item && item.properties) || {};
    return normalizeBool(props._is_upsell)
      || normalizeBool(props._welcome_kit_gift)
      || normalizeBool(props._quantity_welcome_gift)
      || normalizeBool(props._free_gift)
      || normalizeBool(props._free_unlock);
  }

  function getDiscountCode() {
    var override = window.__pdpDiscountOverride;
    if (!override) return '';
    if (override.autoApplyOnSubscription === false) return '';
    if (typeof override.couponCode === 'string' && override.couponCode.trim()) {
      return override.couponCode.trim();
    }
    return '';
  }

  function getOtpDiscountCode() {
    var override = window.__pdpDiscountOverride;
    if (!override) return '';
    if (override.autoApplyOnOtp !== true) return '';
    var code = override.otpCouponCode || override.couponCode || '';
    if (typeof code === 'string' && code.trim()) return code.trim();
    return '';
  }

  // Discount code carried by a free "unlocks" gift line (stamped by
  // PBFCM-PDP-controller.js as _unlock_discount_code on the _free_unlock
  // line). Returned only while that gift line is actually in the cart, so
  // removing the gift drops the code. Applies regardless of subscription.
  function getUnlockDiscountCode(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    var found = '';
    cart.items.some(function (item) {
      var props = (item && item.properties) || {};
      var isFreeUnlock = normalizeBool(props._free_unlock);
      if (isFreeUnlock && typeof props._unlock_discount_code === 'string' && props._unlock_discount_code.trim()) {
        found = props._unlock_discount_code.trim();
        return true;
      }
      return false;
    });
    return found;
  }

  // Per-line Upsell Card Coupon (metaobject field), stamped as
  // _upsell_discount_code on dialog upsell lines. Collected whenever present.
  function getUpsellDiscountCodes(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    var codes = [];
    cart.items.forEach(function (item) {
      if (!item) return;
      var props = item.properties || {};
      var code = props._upsell_discount_code;
      if (typeof code === 'string' && code.trim()) codes.push(code.trim());
    });
    return codes;
  }

  // Per-line subscription discount codes stamped at add-to-cart time
  // (_sub_discount_code), e.g. by the PBFCM product carousel quick-add.
  // Collected only for subscription lines so the configured/global code
  // follows the product into checkout regardless of the current page.
  function getStampedSubscriptionCodes(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    var codes = [];
    cart.items.forEach(function (item) {
      if (!item) return;
      var props = item.properties || {};
      var isSub = isSubscriptionItem(item);
      if (!isSub) return;
      var code = props._sub_discount_code;
      if (props._plan_switcher_from && typeof props._plan_upgrade_code === 'string' && props._plan_upgrade_code.trim()) {
        code = props._plan_upgrade_code;
      }
      if (typeof code === 'string' && code.trim()) codes.push(code.trim());
    });
    return codes;
  }

  function getStampedOtpCodes(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    var codes = [];
    cart.items.forEach(function (item) {
      if (!item || isSubscriptionItem(item) || isGiftOrAddOnItem(item)) return;
      var props = item.properties || {};
      if (!normalizeBool(props[OTP_AUTO_APPLY_PROPERTY_KEY])) return;
      var code = props._otp_discount_code;
      if (typeof code === 'string' && code.trim()) codes.push(code.trim());
    });
    return codes;
  }

  function cartHasSubscription(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      if (!item) return false;
      var props = item.properties || {};
      return normalizeBool(props[SUBSCRIPTION_PROPERTY_KEY])
        || Boolean(item.selling_plan)
        || Boolean(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
    });
  }

  function cartHasOneTimePurchase(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      if (!item || isSubscriptionItem(item) || isGiftOrAddOnItem(item)) return false;
      var props = item.properties || {};
      var byProp = normalizeBool(props[ONE_TIME_PROPERTY_KEY]);
      var byNoPlan = !item.selling_plan && !(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
      return byProp || byNoPlan;
    });
  }

  // Collect every discount code that should apply to this cart.
  function getCheckoutCodes(cart) {
    var codes = [];

    // Section auto-apply code: subscription carts only (existing behavior).
    if (cartHasSubscription(cart)) {
      var sectionCode = getDiscountCode();
      if (sectionCode) codes.push(sectionCode);
    }

    // Section auto-apply code: one-time carts only when the new OTP toggle is enabled.
    if (cartHasOneTimePurchase(cart)) {
      var otpSectionCode = getOtpDiscountCode();
      if (otpSectionCode) codes.push(otpSectionCode);
    }

    // Per-line stamped subscription codes (PBFCM carousel quick-add, etc.).
    getStampedSubscriptionCodes(cart).forEach(function (c) { codes.push(c); });

    // Per-line stamped one-time codes only when the item carries the OTP auto-apply flag.
    getStampedOtpCodes(cart).forEach(function (c) { codes.push(c); });

    // Free-unlock gift code: whenever the gift line is present, regardless of
    // subscription, so the unlocked product is discounted correctly.
    var unlockCode = getUnlockDiscountCode(cart);
    if (unlockCode) codes.push(unlockCode);

    // Upsell Card Coupon from metaobject (extra discount on dialog upsells).
    getUpsellDiscountCodes(cart).forEach(function (c) { codes.push(c); });

    // De-duplicate (a merchant could configure the same code for both).
    return codes.filter(function (c, i) { return c && codes.indexOf(c) === i; });
  }

  // Build a checkout URL that applies EVERY code via the ?discount= query
  // param. Shopify accepts a comma-separated list of codes in this param and
  // applies them all together — the multiple combinable codes stack:
  //   /checkout?discount=A,B,C
  // (The /discount/PATH route, by contrast, only takes one code per request —
  // /discount/A,B is read as a single non-existent code and applies none.)
  // Each code is URL-encoded; the commas separating them stay literal.
  function buildDiscountPath(codes) {
    var value = codes.map(function (c) { return encodeURIComponent(c); }).join(',');
    return '/checkout?discount=' + value;
  }

  // Back-compat: a single navigable URL that applies every resolved code.
  function buildCheckoutUrl(cart) {
    var codes = getCheckoutCodes(cart);
    if (!codes.length) return '/checkout';
    return buildDiscountPath(codes);
  }

  // Apply discount codes and proceed to checkout in one navigation.
  //   0 codes  → /checkout
  //   1+ codes → /checkout?discount=A,B (all codes applied via the query param)
  function applyCodesAndGo(codes) {
    window.location.href = codes && codes.length ? buildDiscountPath(codes) : '/checkout';
    return Promise.resolve();
  }

  function isWholeCart(cart) {
    return Boolean(cart && Array.isArray(cart.items) && typeof cart.item_count === 'number');
  }

  function readCart(force) {
    if (window.FDCartApi && typeof window.FDCartApi.getCart === 'function') {
      return window.FDCartApi.getCart(force);
    }
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function cachedCartState(store) {
    var cached = store && typeof store.getCurrentCart === 'function' ? store.getCurrentCart() : null;
    if (!cached) return plannedCartState();
    var attributes = typeof store.getKnownAttributes === 'function' ? store.getKnownAttributes() : null;
    return { cart: cached, attributes: attributes || cached.attributes || {} };
  }

  // A gift add on its way keeps the attributes and codes of the cart it was planned on, unless the page stamps its auto-applied one-time code on gift lines too.
  function plannedCartState() {
    var gwp = window.FDCartGwp;
    var override = window.__pdpDiscountOverride;
    if (!gwp || typeof gwp.cartBeforeGiftAdd !== 'function' || (override && override.autoApplyOnOtp === true)) return null;
    var planned = gwp.cartBeforeGiftAdd();
    return isWholeCart(planned) ? { cart: planned, attributes: planned.attributes || {} } : null;
  }

  // A gift add on its way is not counted as a write still answering: the checkout extension adds earned gifts itself.
  function writeState(store) {
    var state = store && typeof store.getWriteState === 'function' ? store.getWriteState() : { started: 0, pending: 0 };
    var gwp = window.FDCartGwp;
    var giftAdd = gwp && typeof gwp.giftAddPending === 'function' && gwp.giftAddPending() ? 1 : 0;
    return { started: state.started, pending: Math.max(0, state.pending - giftAdd) };
  }

  // The cart the page already holds when it matches the server, else a fresh read.
  // A write still answering (the SavedBy CHECKOUT+ write) returns that cart: wait
  // up to 1.5s for it before reading. Null while a write is still running, or when
  // one started during the read: that cart can predate it.
  function currentCart() {
    var store = window.FDCart;
    var state = cachedCartState(store);
    if (state) return Promise.resolve(state);
    var pending = writeState(store).pending > 0;
    var settled = pending && typeof store.whenWritesIdle === 'function'
      ? store.whenWritesIdle(1500)
      : Promise.resolve();
    return settled.then(function () {
      if (writeState(store).pending > 0) return null;
      var after = cachedCartState(store);
      if (after) return after;
      var startedBefore = writeState(store).started;
      return readCart(true).then(function (cart) {
        var now = writeState(store);
        if (now.pending > 0 || now.started !== startedBefore) return null;
        return { cart: cart, attributes: (cart && cart.attributes) || {} };
      });
    });
  }

  // The purchase flags a paid cart's lines call for, as the drawer checkout form sets them: any subscription line makes it a subscription purchase.
  function purchaseFlags(cart) {
    if (!isWholeCart(cart) || !cart.items.some(function (item) { return Number(item.final_line_price) > 0; })) return null;
    var subscription = cartHasSubscription(cart);
    if (!subscription && !cartHasOneTimePurchase(cart)) return null;
    var flags = {};
    flags[SUBSCRIPTION_PROPERTY_KEY] = subscription ? 'true' : '';
    flags[ONE_TIME_PROPERTY_KEY] = subscription ? '' : 'true';
    return flags;
  }

  function flagsDiffer(flags, attributes) {
    return Object.keys(flags).some(function (key) {
      return normalizeBool(flags[key]) !== normalizeBool(attributes && attributes[key]);
    });
  }

  // One write of the flags, then checkout with the codes of its answer, or of the cart at hand when it fails.
  function writeFlagsAndGo(flags, cart) {
    var body = new FormData();
    Object.keys(flags).forEach(function (key) {
      body.append('attributes[' + key + ']', flags[key]);
    });
    return fetch('/cart/update.js', { method: 'POST', body: body, credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart update ' + r.status);
        return r.json();
      })
      .then(function (updated) { return applyCodesAndGo(getCheckoutCodes(isWholeCart(updated) ? updated : cart)); })
      .catch(function () { return applyCodesAndGo(getCheckoutCodes(cart)); });
  }

  // cart: the cart the caller's last write returned. Without it the cart is read.
  function redirectToCheckout(cart) {
    var store = window.FDCart;
    var planned = plannedCartState();
    var current = (store && typeof store.getCurrentCart === 'function' ? store.getCurrentCart() : null) || (planned && planned.cart);
    var ready = isWholeCart(cart) ? Promise.resolve(cart) : current ? Promise.resolve(current) : readCart(true);
    return ready
      .then(function (fresh) {
        // A flag write dropped beside another write is put back, only once no cart write is on its way.
        var flags = purchaseFlags(fresh);
        var idle = !store || typeof store.getWriteState !== 'function' || store.getWriteState().pending === 0;
        if (!flags || !idle || !flagsDiffer(flags, fresh.attributes)) return applyCodesAndGo(getCheckoutCodes(fresh));
        return writeFlagsAndGo(flags, fresh);
      })
      .catch(function (err) {
        console.warn(LOG_PREFIX, 'redirectToCheckout: cart read failed, falling back to /checkout', err);
        window.location.href = '/checkout';
      });
  }

  // attributes[...] fields of the checkout form, or null when it carries anything else.
  function formAttributes(formData) {
    var attributes = {};
    var onlyAttributes = true;
    formData.forEach(function (value, name) {
      var match = /^attributes\[(.+)\]$/.exec(name);
      if (!match) {
        onlyAttributes = false;
        return;
      }
      attributes[match[1]] = String(value);
    });
    return onlyAttributes ? attributes : null;
  }

  // A value is already set when it is equal, or when the form sends '' and the
  // cart has no such attribute (Shopify stores '' as unset).
  function attributesToWrite(wanted, current) {
    var changed = {};
    Object.keys(wanted).forEach(function (key) {
      var want = wanted[key];
      var have = current && current[key] != null ? String(current[key]) : null;
      if (have === want) return;
      if (have === null && want === '') return;
      changed[key] = want;
    });
    return changed;
  }

  // Waits for drawer actions still writing (8s), then a gift removal or fix on its way (2s); gift adds are the checkout extension's.
  function drawerSettled() {
    var writes = window.FDCartDrawerWrites;
    var gwp = window.FDCartGwp;
    clearTimeout(busyTimer);
    busyTimer = setTimeout(checkoutDidNotOpen, 10000);
    if (gwp && typeof gwp.holdAddsForCheckout === 'function') gwp.holdAddsForCheckout();
    var actions = !writes || typeof writes.settled !== 'function' || !writes.pending()
      ? Promise.resolve()
      : Promise.race([
        writes.settled(),
        new Promise(function (resolve) { setTimeout(resolve, 8000); })
      ]);
    return actions.then(function () {
      if (!gwp || typeof gwp.whenGiftWriteDone !== 'function' || (gwp.giftAddPending && gwp.giftAddPending())) return null;
      return gwp.whenGiftWriteDone(2000);
    });
  }

  function submitMinicartAndRedirect(form) {
    return drawerSettled()
      .then(function () {
        var formData = new FormData(form);
        formData.delete('checkout');
        var wanted = formAttributes(formData);
        if (!wanted) return postAttributes(formData);
        return currentCart().then(function (state) {
          if (!state || !isWholeCart(state.cart)) return postAttributes(formData);
          var changed = attributesToWrite(wanted, state.attributes);
          if (!Object.keys(changed).length) return applyCodesAndGo(getCheckoutCodes(state.cart));
          var body = new FormData();
          Object.keys(changed).forEach(function (key) {
            body.append('attributes[' + key + ']', changed[key]);
          });
          return postAttributes(body);
        });
      })
      .catch(function (err) {
        console.warn(LOG_PREFIX, 'submitMinicartAndRedirect: cart update failed, falling back to /checkout', err);
        window.location.href = '/checkout';
      });
  }

  function postAttributes(body) {
    return fetch('/cart/update.js', {
      method: 'POST',
      body: body,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return applyCodesAndGo(getCheckoutCodes(cart));
      });
  }

  // Busy state on the element the shopper pressed, from the first frame. No
  // disabled attribute: the SavedBy CHECKOUT+ clone re-submits the form itself.
  var busyTimer = null;

  function markBusy(el) {
    if (!el || !el.classList) return;
    el.classList.add(BUSY_CLASS);
    el.setAttribute('aria-busy', 'true');
    clearTimeout(busyTimer);
    busyTimer = setTimeout(clearBusy, 10000);
  }

  function clearBusy() {
    Array.prototype.forEach.call(document.querySelectorAll('.' + BUSY_CLASS), function (el) {
      el.classList.remove(BUSY_CLASS);
      el.removeAttribute('aria-busy');
    });
  }

  // Back from the browser cache, or still here when the 10s gift add hold ends: the busy look clears and gift adds resume.
  function checkoutDidNotOpen() {
    clearTimeout(busyTimer);
    clearBusy();
    var gwp = window.FDCartGwp;
    if (gwp && typeof gwp.resumeAfterCheckout === 'function') gwp.resumeAfterCheckout();
  }

  // The theme button, or the SavedBy CHECKOUT+ widget: its button lives in a shadow
  // root page CSS cannot reach, so the widget itself shows the busy state.
  function checkoutControlFromEvent(e) {
    var target = e.target;
    if (!target || target.nodeType !== 1) return null;
    var direct = target.closest('button[name="checkout"], #checkoutButton, a.checkout-btn, a[href="/checkout"]');
    if (direct) return direct;
    if (target.localName !== 'savedby-checkout-plus' || typeof e.composedPath !== 'function') return null;
    var path = e.composedPath();
    for (var i = 0; i < path.length && path[i] !== target; i++) {
      var el = path[i];
      if (el.nodeType !== 1) continue;
      var text = el.textContent || '';
      if (text.length < 60 && /checkout/i.test(text)) return target;
    }
    return null;
  }

  document.addEventListener('click', function (e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    markBusy(checkoutControlFromEvent(e));
  }, true);

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) checkoutDidNotOpen();
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (!/^\/?cart(\/|\?|$)/.test(action)) return;
    var submitter = e.submitter;
    var isCheckoutSubmit = submitter && (
      submitter.name === 'checkout' ||
      submitter.id === 'checkoutButton' ||
      (submitter.classList && submitter.classList.contains('new-mini-cart-checkout-button'))
    );
    if (!isCheckoutSubmit) return;
    e.preventDefault();
    markBusy(submitter);
    submitMinicartAndRedirect(form);
  }, true);

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    var target = e.target.closest && e.target.closest('a.checkout-btn, a[href="/checkout"]');
    if (!target) return;
    e.preventDefault();
    drawerSettled().then(function () { return redirectToCheckout(); });
  });

  window.SubscriptionCheckout = {
    getDiscountCode: getDiscountCode,
    getOtpDiscountCode: getOtpDiscountCode,
    getUnlockDiscountCode: getUnlockDiscountCode,
    getStampedSubscriptionCodes: getStampedSubscriptionCodes,
    getStampedOtpCodes: getStampedOtpCodes,
    getCheckoutCodes: getCheckoutCodes,
    cartHasSubscription: cartHasSubscription,
    cartHasOneTimePurchase: cartHasOneTimePurchase,
    buildCheckoutUrl: buildCheckoutUrl,
    applyCodesAndGo: applyCodesAndGo,
    redirectToCheckout: redirectToCheckout
  };
})();
